"use client";

import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import ContactForm from "@/components/forms/ContactForm";
import { Trophy, Gem, Zap, Shield, ArrowRight } from "lucide-react";
import TestimonialsSection from "@/components/sections/TestimonialsSection";
import GoogleMapEmbed from "@/components/media/GoogleMapEmbed";
import ScrollIndicator from "@/components/ui/ScrollIndicator";
import PartnerMarquee from "@/components/sections/PartnerMarquee";
import FAQ from "@/components/sections/FAQ";
import { categoryLabels } from "@/data/portfolio";
import { mainPageFaq } from "@/data/mainPageFaq";
import { partners } from "@/data/partners";
import { pricingTeaser } from "@/data/pricing";
import BlogPostCard from "@/components/blog/BlogPostCard";

interface PortfolioProjectPreview {
  id: string;
  title: string;
  category: "ppf" | "ceramic" | "tinting";
  year?: number;
  image: string;
  slug: string;
  alt: string;
}

interface BlogPostPreview {
  slug: string;
  title: string;
  description: string;
  date: string;
  readingTime: string;
  category: string;
  coverImage: string;
}

interface HomeClientProps {
  portfolioProjects: PortfolioProjectPreview[];
  blogPosts: BlogPostPreview[];
}

// TODO(i18n): сайт целиком на ET. Для рынка Tallinn/Harjumaa стоит
// рассмотреть добавление RU/EN версий через next-intl или App Router i18n routing.

type CategoryFilter = "all" | "ppf" | "ceramic" | "tinting";
type BlogFilter = "all" | "ppf" | "keraamika" | "toonimine" | "hooldus";

const projectFilters: Array<{ value: CategoryFilter; label: string }> = [
  { value: "all", label: "Kõik" },
  { value: "ppf", label: "PPF" },
  { value: "ceramic", label: "Keraamiline kaitse" },
  { value: "tinting", label: "Klaaside toonimine" },
];

const blogFilters: Array<{ value: BlogFilter; label: string }> = [
  { value: "all", label: "Kõik" },
  { value: "ppf", label: "PPF" },
  { value: "keraamika", label: "Keraamika" },
  { value: "toonimine", label: "Toonimine" },
  { value: "hooldus", label: "Hooldus" },
];

const faqVisibleCount = 7;

export default function Home({ portfolioProjects, blogPosts }: HomeClientProps) {
  const [activeProjectFilter, setActiveProjectFilter] = useState<CategoryFilter>("all");
  const [activeBlogFilter, setActiveBlogFilter] = useState<BlogFilter>("all");
  const shouldReduceMotion = useReducedMotion();

  const visibleProjects = useMemo(() => {
    if (activeProjectFilter === "all") return portfolioProjects;
    return portfolioProjects.filter((project) => project.category === activeProjectFilter);
  }, [activeProjectFilter, portfolioProjects]);

  const visibleBlogPosts = useMemo(() => {
    if (activeBlogFilter === "all") return blogPosts;
    return blogPosts.filter((post) => post.category === activeBlogFilter);
  }, [activeBlogFilter, blogPosts]);

  const fadeUpVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  return (
    <main className="min-h-screen bg-primary">
      <Navbar />

      {/* === SECTION 1: Hero === */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.04, opacity: 0.85 }}
          animate={shouldReduceMotion ? { scale: 1, opacity: 0.9 } : { scale: [1, 1.03, 1], opacity: [0.9, 1, 0.9] }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 z-0"
        >
          <Image
            src="/hero.png"
            alt="PPF kiletamine ja keraamiline kaitse auto näide"
            fill
            quality={90}
            sizes="100vw"
            className="object-cover"
            priority
            fetchPriority="high"
          />
        </motion.div>
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top_left,rgba(200,169,119,0.16),transparent_40%)]"></div>
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/35 mix-blend-multiply"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-[120px] pb-24 md:pt-[140px] md:pb-28">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="max-w-2xl"
          >
            <motion.div variants={fadeUpVariant} className="mb-6">
              <span className="inline-block border border-accent/40 bg-primary/70 text-accent uppercase text-[11px] tracking-[0.2em] px-3 py-1 font-body rounded-sm shadow-[0_0_25px_rgba(200,169,119,0.12)]">
                CarPro Certified Studio
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUpVariant}
              className="font-heading font-extrabold text-[40px] md:text-[64px] leading-[1.05] text-text-main mb-6"
            >
              Premium autokaitse keskus – <br className="hidden md:inline" />
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6, ease: "easeOut" }}
                className="text-accent"
              >
                PPF, keraamika ja toonimine Tallinnas ja Peetris
              </motion.span>
            </motion.h1>
            <motion.p
              variants={fadeUpVariant}
              className="font-body text-[18px] md:text-[20px] text-text-muted max-w-[520px] leading-relaxed mb-10"
            >
              Sertifitseeritud CarPro lahendused sõidukitele, mis väärivad püsivat täiuslikkust.
              Peetri alevik, Tallinn.
            </motion.p>
            <motion.div variants={fadeUpVariant} className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link
                href="/kontakt#contact-form"
                className="inline-flex items-center justify-center bg-accent text-primary font-heading font-bold px-8 py-3.5 rounded-sm hover:bg-accent/90 transition-all duration-200 shadow-[0_0_25px_rgba(200,169,119,0.22)]"
              >
                Küsi pakkumist
              </Link>
              <Link
                href="/#teenused"
                className="inline-flex items-center justify-center bg-transparent border border-accent text-accent font-heading font-bold px-8 py-3.5 rounded-sm hover:bg-accent/10 transition-all duration-200"
              >
                Vaata teenuseid
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <ScrollIndicator className="mt-0" />
        </div>
      </section>

      {/* === SECTION 2: Услуги (3 карточки) === */}
      <section id="teenused" className="bg-primary py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            className="mb-16"
          >
            <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
              Meie Teenused
            </span>
            <h2 className="font-heading font-bold text-[36px] md:text-[48px] text-text-main mb-6">
              Meie Professionaalsed Teenused
            </h2>
            <div className="w-[40px] h-[1px] bg-accent"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                id: "01",
                title: "Kaitsekiletamine PPF",
                desc: "CarPro Immortal PPF kaitsekile — isetaastuv, vastupidav, nähtamatu kaitse lakipinnale.",
                image: "/services/Kaitsekiletamine%20PPF.png",
                href: "/teenused/kaitsekiletamine-ppf",
              },
              {
                id: "02",
                title: "Keraamiline kaitse",
                desc: "CarPro Quartz Pro 2020 keraamiline töötlus klaas-, plast-, metall- ja värvpindadele.",
                image: "/services/Keraamiline%20kaitse.png",
                href: "/teenused/keraamiline-kaitse",
              },
              {
                id: "03",
                title: "Klaaside toonimine",
                desc: "CarPro Immortal PPF kilega klaaside toonimine — UV kaitse ja privaatsus stiilselt.",
                image: "/services/Klaaside%20toonimine.png",
                href: "/teenused/klaaside-toonimine",
              },
            ].map((service, idx) => (
              <Link key={service.id} href={service.href} className="group block h-full">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                  className="h-full bg-secondary border border-white/5 border-t-2 border-t-transparent hover:border-t-accent hover:-translate-y-1 transition-all duration-300 rounded-sm flex flex-col overflow-hidden"
                >
                  <div className="relative w-full aspect-[16/9] overflow-hidden bg-primary/20">
                    <Image
                      src={service.image}
                      alt={service.title === "Kaitsekiletamine PPF" ? "Kaitsekiletamine PPF – CarPro Immortal" : service.title === "Keraamiline kaitse" ? "Keraamiline kaitse – CarPro Quartz Pro 2020" : "Klaaside toonimine – privaatsus ja UV-kaitse"}
                      fill
                      quality={80}
                      sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors duration-500"></div>
                  </div>
                  <div className="p-8 flex flex-col grow relative">
                    <span className="absolute top-6 right-8 text-accent font-heading font-extrabold text-[48px] opacity-10 select-none">
                      {service.id}
                    </span>
                    <h3 className="font-heading font-bold text-[22px] text-text-main mb-4 relative z-10">
                      {service.title}
                    </h3>
                    <p className="font-body text-[15px] text-text-muted mb-8 relative z-10 leading-relaxed">
                      {service.desc}
                    </p>
                    <div className="mt-auto relative z-10">
                      <span className="inline-flex items-center gap-2 text-accent font-body font-medium transition-all group-hover:gap-3">
                        Loe lähemalt <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === NEW: PORTFOLIO === */}
      <section className="bg-secondary py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-4"
          >
            <div>
              <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
                Meie tööd
              </span>
              <h2 className="font-heading font-bold text-[36px] md:text-[48px] text-text-main mb-4">
                Viimased projektid
              </h2>
            </div>
            <Link href="/tood" className="inline-flex items-center gap-2 text-accent font-body font-medium hover:underline underline-offset-4 transition-all">
              Vaata kõiki töid <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="mb-8 flex flex-wrap gap-2 sm:gap-3">
            {projectFilters.map((filter) => {
              const isActive = filter.value === activeProjectFilter;

              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setActiveProjectFilter(filter.value)}
                  aria-pressed={isActive}
                  className={`whitespace-nowrap rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-300 sm:px-6 sm:py-3 ${isActive
                      ? "bg-accent text-primary"
                      : "border border-white/10 bg-primary/70 text-text-main hover:border-accent/40 hover:bg-accent hover:text-primary"
                    }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          {visibleProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {visibleProjects.map((project, idx) => (
                <Link key={project.id} href={`/tood/${project.slug}`} className="block group h-full">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.15 }}
                    className="h-full bg-primary border border-white/5 rounded-sm overflow-hidden hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="relative w-full aspect-[4/3] overflow-hidden bg-primary/20">
                      <Image
                        src={project.image}
                        alt={project.alt || project.title}
                        fill
                        quality={80}
                        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                        className="object-cover grayscale-[60%] brightness-90 transition-[filter,transform] duration-300 ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105"
                      />
                      <div className="absolute top-3 right-3 bg-accent/90 text-primary text-xs font-bold px-2 py-1 rounded-sm">
                        {categoryLabels[project.category]}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading font-bold text-[20px] text-text-main mb-2 group-hover:text-accent transition-colors">
                        {project.title}
                      </h3>
                      <p className="font-body text-sm text-text-muted">{project.year ?? "2024"}</p>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-sm border border-dashed border-white/10 bg-primary/70 p-8 text-center text-sm text-text-muted">
              Selles kategoorias pole praegu veel töid.
            </div>
          )}
        </div>
      </section>

      {/* === NEW: PRICING TEASER === */}
      <section className="bg-primary py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-4"
          >
            <div>
              <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
                Hinnakiri
              </span>
              <h2 className="font-heading font-bold text-[36px] md:text-[48px] text-text-main mb-4">
                Läbipaistvad hinnad
              </h2>
            </div>
            <Link href="/hinnakiri" className="inline-flex items-center gap-2 text-accent font-body font-medium hover:underline underline-offset-4 transition-all">
              Vaata kogu hinnakirja <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingTeaser.map((item, idx) => (
              <Link key={item.service} href={item.link} className="block group h-full">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="h-full bg-secondary border border-white/5 rounded-sm p-8 text-center hover:border-accent/40 transition-colors"
                >
                  <h3 className="font-heading font-bold text-[22px] text-text-main mb-4">
                    {item.service}
                  </h3>
                  <p className="font-heading font-extrabold text-[32px] text-accent mb-6">
                    {item.price}
                  </p>
                  <span className="inline-block bg-transparent border border-white/20 text-text-main font-body font-medium px-6 py-2 rounded-sm group-hover:border-accent group-hover:text-accent transition-all">
                    Vaata teenust
                  </span>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === NEW: BLOG === */}
      <section className="bg-secondary py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-4"
          >
            <div>
              <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
                Blogi
              </span>
              <h2 className="font-heading font-bold text-[36px] md:text-[48px] text-text-main mb-4">
                Viimased artiklid
              </h2>
            </div>
            <Link href="/blog" className="inline-flex items-center gap-2 text-accent font-body font-medium hover:underline underline-offset-4 transition-all">
              Loe kõiki artikleid <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="mb-8 flex flex-wrap gap-2 sm:gap-3">
            {blogFilters.map((filter) => {
              const isActive = filter.value === activeBlogFilter;

              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setActiveBlogFilter(filter.value)}
                  aria-pressed={isActive}
                  className={`whitespace-nowrap rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-300 sm:px-6 sm:py-3 ${
                    isActive
                      ? "bg-accent text-primary"
                      : "border border-white/10 bg-primary/70 text-text-main hover:border-accent/40 hover:bg-accent hover:text-primary"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          {visibleBlogPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {visibleBlogPosts.map((post, idx) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                >
                  <BlogPostCard
                    title={post.title}
                    description={post.description}
                    slug={post.slug}
                    category={post.category as "ppf" | "keraamika" | "toonimine" | "hooldus"}
                    date={post.date}
                    readingTime={post.readingTime}
                    coverImage={post.coverImage}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="rounded-sm border border-dashed border-white/10 bg-primary/70 p-8 text-center text-sm text-text-muted">
              Selles kategoorias pole praegu veel artikleid.
            </div>
          )}
        </div>
      </section>

      <section className="bg-primary py-20 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUpVariant}
            className="mb-10 text-center"
          >
            <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
              Partnerid
            </span>
            <h2 className="font-heading font-bold text-[32px] md:text-[40px] text-text-main mb-4">
              Usaldusväärsed kaubamärgid meie töös
            </h2>
            <p className="font-body text-[16px] text-text-muted max-w-2xl mx-auto">
              Oleme valinud koostööpartnerid, kes jagavad meie väärtusi kvaliteedi, täpsuse ja pikaajalise kaitse osas.
            </p>
          </motion.div>
    </div>

    <PartnerMarquee partners={partners} />
      </section>

      <TestimonialsSection />

      {/* === SECTION 4: Statistika / miks valida meid === */}
      <section className="bg-secondary border-y border-accent/15 py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 pb-20 border-b border-white/5">
            {[
              { num: "25+", label: "Aastat kogemust" },
              { num: "500+", label: "Teostatud tööd" },
              { num: "100%", label: "CarPro sertifitseeritud" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex flex-col items-center md:items-start text-center md:text-left"
              >
                <span className="font-heading font-extrabold text-[48px] md:text-[56px] text-accent leading-none mb-2">
                  {stat.num}
                </span>
                <span className="font-body text-[13px] text-text-muted uppercase tracking-widest">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariant}
              className="lg:col-span-5"
            >
              <span className="text-accent uppercase text-[12px] tracking-[0.15em] font-bold font-body block mb-4">
                Miks valida meid
              </span>
              <h2 className="font-heading font-bold text-[36px] text-text-main mb-6 leading-tight">
                Miks Valida PPF Garage?
              </h2>
              <p className="font-body text-[16px] text-text-muted mb-10 leading-relaxed">
                Oleme pühendunud pakkuma kõrgeima taseme sõidukikaitse teenuseid, ühendades
                aastakümnete pikkuse kogemuse valdkonna juhtivate innovatsioonidega, nagu CarPro.
                Teie auto väärib ainult parimat hoolt.
              </p>
              <Link
                href="/meist"
                className="bg-transparent border border-white/20 text-text-main font-heading font-bold px-6 py-3 rounded-sm hover:border-accent hover:text-accent transition-colors duration-200 inline-flex items-center gap-2"
              >
                Meist lähemalt <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12"
            >
              {[
                {
                  icon: Trophy,
                  title: "Parimad spetsialistid",
                  desc: "Meie meeskond on läbinud range koolituse ja omab rahvusvahelisi sertifikaate.",
                },
                {
                  icon: Gem,
                  title: "Mõistlik hind ja kvaliteet",
                  desc: "Pakume läbipaistvat hinnakujundust kompromisse tegemata töö kvaliteedis.",
                },
                {
                  icon: Zap,
                  title: "Kiire töö ja teenindus",
                  desc: "Väärtustame teie aega, teostades töid efektiivselt ja kokkulepitud tähtaegadel.",
                },
                {
                  icon: Shield,
                  title: "Usaldusväärne teenusepakkuja",
                  desc: "Anname oma töödele ja materjalidele ametliku ning pikaajalise garantii.",
                },
              ].map((feature, i) => (
                <motion.div key={i} variants={fadeUpVariant} className="flex flex-col items-start">
                  <div className="w-12 h-12 rounded bg-primary border border-white/5 flex items-center justify-center mb-5 text-accent">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading font-semibold text-[18px] text-text-main mb-3">
                    {feature.title}
                  </h3>
                  <p className="font-body text-[15px] text-text-muted leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* === SECTION 5: CarPro tooted === */}
      <section className="bg-primary/30 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="mb-12 text-center"
          >
            <h2 className="font-heading text-[28px] md:text-[32px] text-text-main">
              Meie tooted
            </h2>
          </motion.div>

          {/* Horizontal scroll on mobile, grid on desktop */}
          <div className="relative flex overflow-x-auto pb-8 md:pb-0 md:grid md:grid-cols-4 gap-6 snap-x snap-mandatory hide-scrollbar">
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-primary/30 to-transparent md:hidden" />
            {[
              {
                name: "Window Film",
                desc: "Kuumust peegeldav kile",
                image: "/services/CarPro%20tooteperekond/Window%20Film.png",
              },
              {
                name: "D.Quartz",
                desc: "Teemanditugevusega nanokaitse",
                image: "/services/CarPro%20tooteperekond/D.Quartz.png",
              },
              {
                name: "Immortal PPF",
                desc: "Isetaastuv kaitsekile",
                image: "/services/CarPro%20tooteperekond/Immortal%20PPF.png",
              },
              {
                name: "C-Quartz UK",
                desc: "Keraamiline kaitse põhjamaale",
                image: "/services/CarPro%20tooteperekond/C-Quartz%20UK.png",
              },
            ].map((product, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="min-w-[260px] md:min-w-0 flex-shrink-0 snap-start bg-secondary border border-white/5 rounded-sm overflow-hidden group"
              >
                <div className="relative w-full aspect-square overflow-hidden bg-primary/20">
                  <Image
                    src={product.image}
                    alt={product.name === "Window Film" ? "CarPro Window Film – toonitud klaaside kaitse" : product.name === "D.Quartz" ? "CarPro D.Quartz – nanokaitse ja läige" : product.name === "Immortal PPF" ? "CarPro Immortal PPF – isetaastuv kaitsekile" : "CarPro C-Quartz UK – keraamiline kaitse"}
                    fill
                    quality={80}
                    sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 100vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors duration-500"></div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-[18px] text-text-main mb-2">
                    {product.name}
                  </h3>
                  <p className="font-body text-[14px] text-text-muted">
                    {product.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* === SECTION : PPF Garage – sinu usaldusväärne partner auto kaitsmisel === */}
      <section className="bg-primary py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-[28px] md:text-[32px] text-text-main text-center mb-10">
            PPF Garage – sinu usaldusväärne partner auto kaitsmisel
          </h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="border border-white/5 bg-secondary/90 p-8 md:p-10 shadow-xl shadow-black/20"
          // убрал rounded-xl
          >
            <p className="text-[16px] leading-relaxed text-text-muted mb-4">
              Pakume <Link href="/teenused/kaitsekiletamine-ppf" className="text-accent hover:underline">auto kiletamist</Link> (PPF), <Link href="/teenused/keraamiline-kaitse" className="text-accent hover:underline">keraamilist kaitset</Link> ja <Link href="/teenused/klaaside-toonimine" className="text-accent hover:underline">klaaside toonimist</Link> Tallinnas, Peetris, Keilas ja laiemalt Harjumaal. Meie töötab koos klientidega järjepidevalt alates esimesest konsultatsioonist kuni lõpuks üleandmiseks, et kaitse oleks täpselt sobiv ja hoolduselt lihtne.
            </p>
            <p className="text-[16px] leading-relaxed text-text-muted mb-4">
              <strong className="text-text-main">PPF kaitsekile</strong> ja <strong className="text-text-main">keraamiline kaitse</strong> paigaldatakse töökojas ettevaatlikult, et vältida kortsude, tolmu ja ebatasase pinna teket. <strong className="text-text-main">Klaaside toonimine</strong> on mõeldud neile, kes soovivad lisada privaatsust, vähendada päikese koormust ja hoida salongi mugavamana ka kuumadel päevadel.
            </p>
            <p className="text-[16px] leading-relaxed text-text-muted">
              Iga töö lõpetame detailide kontrolliga ja anname kliendile selged juhised hoolduseks. Vaata meie <Link href="/tood" className="text-accent hover:underline">töid</Link> ja <Link href="/hinnakiri" className="text-accent hover:underline">hindu</Link>, ning võta <Link href="/kontakt" className="text-accent hover:underline">ühendust</Link> – vastame 24 tunni jooksul!
            </p>
          </motion.div>
        </div>
      </section>

      {/* === SECTION 6: Tööprotsess === */}
      <section className="bg-gradient-to-b from-secondary to-primary/20 py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="mb-20 text-center"
          >
            <h2 className="font-heading font-bold text-[36px] md:text-[48px] text-text-main mb-4">
              Kuidas me töötame
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative">
            {[
              {
                num: "1",
                title: "Konsultatsioon",
                desc: "Kaardistame sõiduki seisukorra ja leiame sobivaima lahenduse.",
              },
              {
                num: "2",
                title: "Pinna ettevalmistus",
                desc: "Süvapesu, savitamine ja poleerimine ideaalse pinna saavutamiseks.",
              },
              {
                num: "3",
                title: "Kaitse paigaldamine",
                desc: "Sertifitseeritud spetsialistid kannavad peale kile või keraamika.",
              },
              {
                num: "4",
                title: "Kontroll ja üleandmine",
                desc: "Kvaliteedikontroll ja hooldusjuhiste tutvustamine kliendile.",
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="relative z-10 flex flex-col items-center md:items-start text-center md:text-left"
              >
                {/* Квадратный блок для цифры */}
                <div className="w-14 h-14 flex items-center justify-center font-heading font-extrabold text-[24px] text-accent mb-6 mx-auto md:mx-0 border-2 border-accent/40 bg-primary/10">
                  {step.num}
                </div>
                <h3 className="font-heading font-bold text-[20px] text-text-main mb-3">
                  {step.title}
                </h3>
                <p className="font-body text-[15px] text-text-muted leading-relaxed">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* === SECTION 7: FAQ === */}
      <section className="py-24 px-6 bg-secondary border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <FAQ
            items={mainPageFaq}
            visibleCount={faqVisibleCount}
            title="Korduma kippuvad küsimused"
            subtitle="Küsimused teenuste ja kaitse kohta"
          />
        </div>
      </section>

      {/* === SECTION 8: CTA Banner === */}
      <section className="bg-gradient-to-r from-secondary to-primary border-y border-accent/20 py-24 px-6 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariant}
          className="max-w-3xl mx-auto"
        >
          <h2 className="font-heading font-bold text-[32px] md:text-[42px] text-text-main mb-6 leading-tight">
            Kas Teie sõiduk väärib parimat kaitset?
          </h2>
          <p className="font-body text-[18px] text-text-muted mb-10">
            Küsige pakkumist juba täna — vastame 24 tunni jooksul.
          </p>
          <Link
            href="/kontakt#contact-form"
            className="inline-flex items-center justify-center bg-accent text-primary font-heading font-bold px-8 py-4 rounded-sm hover:bg-accent/90 transition-colors duration-200"
          >
            Küsi pakkumist
          </Link>
        </motion.div>
      </section>

      {/* === SECTION 9: Kontaktivorm === */}
      <section className="bg-primary py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            className="mb-16"
          >
            <h2 className="font-heading font-bold text-[36px] md:text-[48px] text-text-main mb-4">
              Võtke meiega ühendust
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Info & Map */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariant}
            >
              <div className="flex flex-col gap-8 font-body text-[16px] text-text-muted mb-12">
                <div>
                  <h4 className="text-text-main font-semibold mb-2">Aadress</h4>
                  <p>Tähnase tee 1, Peetri alevik, 75312, Harjumaa, Eesti</p>
                </div>
                <div>
                  <h4 className="text-text-main font-semibold mb-2">Kontakt</h4>
                  <a href="tel:+3726803510" className="block hover:text-accent transition-colors">+372 680 3510</a>
                  <a href="mailto:info@ppf.ee" className="block hover:text-accent transition-colors">info@ppf.ee</a>
                </div>
                <div>
                  <h4 className="text-text-main font-semibold mb-2">Lahtiolekuajad</h4>
                  <p>E–R: 09:00 – 18:00</p>
                  <p>L–P: Suletud</p>
                </div>
              </div>

              <GoogleMapEmbed
                title="Asukoht ja ligipääs"
                description="Tähnase tee 1, Peetri alevik, 75312, Harjumaa"
                className="mt-2 w-full"
                iframeClassName="h-[280px] md:h-[320px] w-full"
              />
            </motion.div>

            {/* Right: Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariant}
              className="bg-secondary border border-white/5 p-8 md:p-12 rounded-sm"
            >
              <ContactForm showTitle={false} />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      <Script
        id="faq-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: mainPageFaq.slice(0, faqVisibleCount).map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          }),
        }}
      />
      <Script
        id="local-business-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            name: "PPF Garage",
            image: "https://ppf.ee/hero.png",
            url: "https://ppf.ee",
            telephone: "+3726803510",
            email: "info@ppf.ee",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Tähnase tee 1",
              addressLocality: "Peetri alevik",
              postalCode: "75312",
              addressRegion: "Harjumaa",
              addressCountry: "EE",
            },
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
            priceRange: "€€€",
            // TODO: подключить реальный рейтинг из TestimonialsSection/Google.
          }),
        }}
      />
    </main>
  );
}
